import React, { useEffect, useRef, useState, useCallback } from "react";
import { makeStyles, IconButton, CircularProgress } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { i18n } from "../../../translate/i18n";
import { buildInsightText, sortInsightsForDisplay } from "../../../helpers/buildInsightText";

// Auto-rotate interval (change this constant to adjust)
const TV_INTERVAL_MS = 20000;

const WA_GREEN = "#00a884";
const TV_BG    = "#0d1117";
const TV_CARD  = "#161b22";
const TV_BORDER = "rgba(255,255,255,0.08)";

const pct = (frac) => `${Math.round((frac ?? 0) * 100)}%`;

// ── Typography scale constants (CSS clamp — tuned for 1920×1080, safe at 1366×768) ──
// vmin at 1920×1080 = 10.8px · vmin at 1366×768 = 7.68px
const T = {
  kpiValue:    "clamp(2rem,   6.5vmin, 8rem)",    // KPI top-row numbers
  kpiLabel:    "clamp(0.7rem, 1.3vmin, 1.3rem)",  // KPI top-row labels
  slideTitle:  "clamp(1.1rem, 2.2vmin, 2.5rem)",  // header slide name
  zoneTitle:   "clamp(0.65rem,1.1vmin, 1.1rem)",  // zone section header
  barLabel:    "clamp(0.85rem,1.5vmin, 1.5rem)",  // funnel bar label text
  barValue:    "clamp(1rem,   1.8vmin, 1.8rem)",  // funnel bar count
  outcomeNum:  "clamp(1.4rem, 4vmin,   4rem)",    // satisfecho/reclamo/sinResp
  outcomeLabel:"clamp(0.75rem,1.3vmin, 1.3rem)",  // outcome sub-label
  rateLine:    "clamp(0.85rem,1.5vmin, 1.5rem)",  // tasa resp / tasa accion
  recoText:    "clamp(1rem,   1.8vmin, 1.8rem)",  // warning reco lines
  recoIcon:    "clamp(1.1rem, 2vmin,   2rem)",    // ⚠ icon
  chipText:    "clamp(0.9rem, 1.5vmin, 1.5rem)",  // objeto/tags chips
  rollupNum:   "clamp(2.5rem, 6vmin,   7rem)",    // rollup slide big numbers
  rollupLabel: "clamp(0.8rem, 1.5vmin, 1.5rem)",  // rollup label
  rollupTitle: "clamp(1.5rem, 3vmin,   3.5rem)",  // rollup title
  tableCell:   "clamp(0.9rem, 1.6vmin, 1.6rem)",  // rollup table cells
  tableTh:     "clamp(0.7rem, 1.2vmin, 1.2rem)",  // rollup table headers
};

// ── Inline renderers (no MUI Paper — dark-theme safe) ────────────────────────

const TVBar = ({ label, v, max }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "clamp(6px,1vw,14px)" }}>
    <div style={{
      width: "clamp(80px,14vw,160px)",
      fontSize: T.barLabel,
      color: "rgba(255,255,255,0.65)",
      flexShrink: 0,
      lineHeight: 1.3,
    }}>
      {label}
    </div>
    <div style={{
      flex: 1,
      height: "clamp(14px,2.5vh,28px)",
      background: "rgba(255,255,255,0.08)",
      borderRadius: 4,
      overflow: "hidden",
    }}>
      <div style={{
        width: `${Math.round((v / Math.max(max, 1)) * 100)}%`,
        height: "100%",
        background: WA_GREEN,
      }} />
    </div>
    <div style={{
      width: "clamp(30px,5vw,60px)",
      textAlign: "right",
      fontSize: T.barValue,
      fontWeight: 700,
      color: "#fff",
    }}>
      {v}
    </div>
  </div>
);

// Fills zone height; 3 sections distributed with space-between
const TVFunnelContent = ({ stats }) => {
  const ob = stats?.outcomeBreakdown;
  const enviados    = stats?.enviados ?? 0;
  const respondieron = stats?.respondieron ?? 0;
  const clasificados = (ob?.satisfecho?.count ?? 0) + (ob?.reclamo?.count ?? 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "space-between" }}>

      {/* Funnel bars */}
      <div style={{ display: "flex", flexDirection: "column", gap: "clamp(6px,1.2vh,14px)" }}>
        <TVBar label={i18n.t("aiAssistantStats.funnel.enviados")}     v={enviados}     max={enviados} />
        <TVBar label={i18n.t("aiAssistantStats.funnel.respondieron")} v={respondieron} max={enviados} />
        <TVBar label={i18n.t("aiAssistantStats.funnel.clasificados")} v={clasificados} max={enviados} />
      </div>

      {/* Outcome numbers */}
      <div style={{ display: "flex", gap: "clamp(8px,1.5vw,20px)" }}>
        {[
          { label: i18n.t("aiAssistantStats.kpis.satisfechos"),  node: ob?.satisfecho,   color: WA_GREEN  },
          { label: i18n.t("aiAssistantStats.kpis.reclamos"),     node: ob?.reclamo,      color: "#e53935" },
          { label: i18n.t("aiAssistantStats.kpis.sinRespuesta"), node: ob?.sin_respuesta,color: "#78909c" },
        ].map(({ label, node, color }) => (
          <div key={label} style={{ flex: 1 }}>
            <div style={{ fontSize: T.outcomeNum, fontWeight: 700, color, lineHeight: 1 }}>
              {node?.count ?? 0}
            </div>
            <div style={{ fontSize: T.outcomeLabel, color: "rgba(255,255,255,0.55)", marginTop: "0.25em" }}>
              {label}
            </div>
            <div style={{ fontSize: "clamp(0.7rem,1.2vmin,1.2rem)", color: "rgba(255,255,255,0.32)" }}>
              {pct(node?.pctTotal)}
            </div>
          </div>
        ))}
      </div>

      {/* Rates */}
      <div style={{ display: "flex", gap: "clamp(12px,2vw,28px)", flexWrap: "wrap" }}>
        {[
          { label: i18n.t("aiAssistantStats.kpis.tasaRespuesta"), value: pct(stats?.tasaRespuesta)   },
          { label: i18n.t("aiAssistantStats.kpis.tasaAccion"),    value: pct(stats?.tasaAccionSofia) },
        ].map(({ label, value }) => (
          <span key={label} style={{ fontSize: T.rateLine, color: "rgba(255,255,255,0.55)" }}>
            {label}:{" "}
            <strong style={{ color: "#fff" }}>{value}</strong>
          </span>
        ))}
      </div>
    </div>
  );
};

// Fills zone height; warnings distributed vertically
const TVRecoContent = ({ insights }) => {
  // Tono pro-Sofía: logros (success, verde 🟢) primero, luego oportunidades
  // (warning/info, azul 🔵). Mismo criterio que RecommendationsPanel.
  const list = sortInsightsForDisplay(insights);

  if (list.length === 0) {
    return (
      <div style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: T.recoText,
        color: "rgba(255,255,255,0.28)",
        textAlign: "center",
      }}>
        {i18n.t("aiAssistantStats.empty.noData")}
      </div>
    );
  }

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      justifyContent: "space-around",
    }}>
      {list.map((ins, idx) => {
        const isSuccess = ins.severity === "success";
        return (
          <div key={idx} style={{
            display: "flex",
            gap: "clamp(8px,1.2vw,14px)",
            alignItems: "flex-start",
            fontSize: T.recoText,
            color: "rgba(255,255,255,0.85)",
            lineHeight: 1.55,
          }}>
            <span style={{
              color: isSuccess ? "#00d9a3" : "#4d9fff",
              flexShrink: 0,
              fontSize: T.recoIcon,
            }}>
              {isSuccess ? "🟢" : "💡"}
            </span>
            <span>{buildInsightText(ins)}</span>
          </div>
        );
      })}
    </div>
  );
};

// Fills zone height; objeto + tags distributed
const TVObjetoTagsContent = ({ stats }) => {
  const items     = stats?.objetoReclamoBreakdown ?? [];
  const interesado = stats?.interesadoComprar;
  const hasObjeto = items.length > 0;
  const hasTags   = !!interesado;

  if (!hasObjeto && !hasTags) {
    return (
      <div style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: T.recoText,
        color: "rgba(255,255,255,0.28)",
        textAlign: "center",
      }}>
        {i18n.t("aiAssistantStats.empty.noData")}
      </div>
    );
  }

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      justifyContent: "space-around",
      gap: "clamp(12px,2vh,28px)",
    }}>
      {hasObjeto && (
        <div>
          <div style={{
            fontSize: T.zoneTitle,
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.4)",
            marginBottom: "clamp(6px,1.2vh,14px)",
            letterSpacing: 0.9,
            fontWeight: 700,
          }}>
            {i18n.t("aiAssistantStats.kpis.objetoReclamo")}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "clamp(5px,0.8vw,10px)" }}>
            {items.slice(0, 6).map((o, i) => (
              <span key={i} style={{
                background: "rgba(255,255,255,0.1)",
                borderRadius: 20,
                padding: "clamp(4px,0.7vh,10px) clamp(10px,1.4vw,20px)",
                fontSize: T.chipText,
                color: "#fff",
              }}>
                {o.objeto ?? "?"} ({o.count ?? 0})
              </span>
            ))}
          </div>
        </div>
      )}
      {hasTags && (
        <div>
          <div style={{
            fontSize: T.zoneTitle,
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.4)",
            marginBottom: "clamp(6px,1.2vh,14px)",
            letterSpacing: 0.9,
            fontWeight: 700,
          }}>
            {i18n.t("aiAssistantStats.kpis.tagsTrackeados")}
          </div>
          <span style={{
            background: (interesado.count ?? 0) > 0 ? WA_GREEN : "rgba(255,255,255,0.1)",
            borderRadius: 20,
            padding: "clamp(5px,1vh,14px) clamp(14px,2vw,26px)",
            fontSize: T.chipText,
            color: "#fff",
            fontWeight: 600,
          }}>
            {i18n.t("aiAssistantStats.kpis.interesadoComprar")}: {interesado.count ?? 0}
          </span>
        </div>
      )}
    </div>
  );
};

// ── makeStyles — grid layout ──────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  // ── Outer overlay: CSS grid, 2 rows (header / body) ───────────────────────
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: TV_BG,
    color: "#fff",
    fontFamily: "inherit",
    display: "grid",
    gridTemplateRows: "auto 1fr",
    overflow: "hidden",
  },

  // ── Row 1: slim header ────────────────────────────────────────────────────
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "clamp(6px,1vh,14px) clamp(16px,2.5vw,32px)",
    borderBottom: `1px solid ${TV_BORDER}`,
    flexShrink: 0,
  },
  slideTitle: {
    fontSize: T.slideTitle,
    fontWeight: 700,
    color: "#fff",
    letterSpacing: 0.3,
  },
  dotsRow: { display: "flex", gap: "clamp(6px,1vw,12px)", alignItems: "center" },
  dot: {
    width: "clamp(7px,1.2vmin,14px)",
    height: "clamp(7px,1.2vmin,14px)",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.25)",
    cursor: "pointer",
    transition: "background 0.2s",
    flexShrink: 0,
  },
  dotActive: { background: WA_GREEN },
  exitBtn: { color: "#aaa" },

  // ── Row 2 variant A: centered (loading / rollup / empty) ──────────────────
  bodyCenter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    padding: "clamp(16px,3vh,40px) clamp(24px,4vw,60px)",
    gap: "clamp(16px,3vh,40px)",
    minHeight: 0,
  },

  // ── Row 2 variant B: assistant slide (inner grid: 22vh KPI / 1fr zones) ───
  assistantBody: {
    display: "grid",
    gridTemplateRows: "minmax(0, 22vh) 1fr",
    overflow: "hidden",
    minHeight: 0,
  },

  // ── KPI section (assistant row 1) — 4 cards horizontal ───────────────────
  kpiSection: {
    display: "flex",
    gap: "clamp(8px,1.2vw,18px)",
    padding: "clamp(8px,1.5vh,18px) clamp(16px,2.5vw,32px) clamp(6px,1vh,12px)",
    overflow: "hidden",
    minHeight: 0,
  },
  kpiCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "clamp(8px,1.5vh,18px) clamp(10px,1.5vw,20px)",
    background: TV_CARD,
    borderRadius: 12,
    border: `1px solid ${TV_BORDER}`,
    overflow: "hidden",
    minWidth: 0,
  },
  kpiCardBig: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "clamp(8px,1.5vh,18px) clamp(10px,1.5vw,20px)",
    background: WA_GREEN,
    borderRadius: 12,
    overflow: "hidden",
    minWidth: 0,
  },
  kpiLabel: {
    fontSize: T.kpiLabel,
    textTransform: "uppercase",
    letterSpacing: 0.7,
    color: "rgba(255,255,255,0.65)",
    marginBottom: "0.3em",
    lineHeight: 1.2,
  },
  kpiValue: {
    fontSize: T.kpiValue,
    fontWeight: 800,
    color: "#fff",
    lineHeight: 1,
  },

  // ── Zones section (assistant row 2) — 3 columns ───────────────────────────
  zonesRow: {
    display: "flex",
    gap: "clamp(8px,1.2vw,16px)",
    padding: "0 clamp(16px,2.5vw,32px) clamp(12px,2vh,22px)",
    overflow: "hidden",
    minHeight: 0,
  },
  zone: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    background: TV_CARD,
    border: `1px solid ${TV_BORDER}`,
    borderRadius: 12,
    padding: "clamp(10px,1.8vh,20px) clamp(12px,1.5vw,22px)",
    overflow: "hidden",
    minWidth: 0,
  },
  zoneTitle: {
    fontSize: T.zoneTitle,
    textTransform: "uppercase",
    letterSpacing: 0.9,
    color: "rgba(255,255,255,0.38)",
    marginBottom: "clamp(8px,1.5vh,18px)",
    fontWeight: 700,
    flexShrink: 0,
  },
  zoneContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minHeight: 0,
  },

  // ── Rollup slide ──────────────────────────────────────────────────────────
  rollupTitle: {
    fontSize: T.rollupTitle,
    fontWeight: 700,
    color: "#fff",
    textAlign: "center",
  },
  rollupKpiRow: {
    display: "flex",
    gap: "clamp(16px,2.5vw,40px)",
    flexWrap: "wrap",
    justifyContent: "center",
    width: "100%",
  },
  rollupKpiItem: {
    textAlign: "center",
    background: TV_CARD,
    border: `1px solid ${TV_BORDER}`,
    borderRadius: 14,
    padding: "clamp(14px,2.5vh,30px) clamp(20px,3.5vw,50px)",
    minWidth: "clamp(130px,16vw,220px)",
  },
  rollupKpiValue: {
    fontSize: T.rollupNum,
    fontWeight: 800,
    color: "#fff",
    lineHeight: 1,
    marginBottom: "0.3em",
  },
  rollupKpiLabel: {
    fontSize: T.rollupLabel,
    color: "rgba(255,255,255,0.55)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  rollupTable: {
    width: "100%",
    maxWidth: "min(700px,90vw)",
    borderCollapse: "collapse",
    fontSize: T.tableCell,
  },
  rollupTh: {
    color: "rgba(255,255,255,0.38)",
    fontSize: T.tableTh,
    textTransform: "uppercase",
    fontWeight: 600,
    padding: "clamp(4px,0.8vh,12px) clamp(10px,1.5vw,18px)",
    borderBottom: `1px solid ${TV_BORDER}`,
    textAlign: "left",
  },
  rollupTd: {
    color: "rgba(255,255,255,0.85)",
    padding: "clamp(4px,0.8vh,12px) clamp(10px,1.5vw,18px)",
    borderBottom: `1px solid ${TV_BORDER}`,
  },
}));

// ── TVOverlay main ────────────────────────────────────────────────────────────

const TVOverlay = ({ visible, onExit, rollup, tvStats, tvInsights, show, loading }) => {
  const classes = useStyles();
  const [slide, setSlide] = useState(0);
  const pausedRef = useRef(false);
  const overlayRef = useRef(null);

  const perAssistant = rollup?.perAssistant ?? [];
  const hasMultiple  = perAssistant.length > 1;

  // slides: [rollup, ...assistants] when multiple; [single] otherwise
  const slides = hasMultiple
    ? [{ type: "rollup" }, ...perAssistant.map((a) => ({ type: "assistant", ...a }))]
    : perAssistant.length === 1
    ? [{ type: "assistant", ...perAssistant[0] }]
    : [];

  const nextSlide = useCallback(() => {
    setSlide((s) => (s + 1) % Math.max(slides.length, 1));
  }, [slides.length]);

  // Auto-rotate (multiple slides only)
  useEffect(() => {
    if (!visible || !hasMultiple) return;
    const id = setInterval(() => {
      if (!pausedRef.current) nextSlide();
    }, TV_INTERVAL_MS);
    return () => clearInterval(id);
  }, [visible, hasMultiple, nextSlide]);

  // Reset to first slide on open
  useEffect(() => {
    if (visible) setSlide(0);
  }, [visible]);

  // Fullscreen request
  useEffect(() => {
    if (!visible) return;
    const el = overlayRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => {});
  }, [visible]);

  // App-level ESC handler
  useEffect(() => {
    if (!visible) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        document.exitFullscreen?.().catch(() => {});
        onExit();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [visible, onExit]);

  // Native fullscreenchange (browser Esc or F11)
  useEffect(() => {
    if (!visible) return;
    const handler = () => { if (!document.fullscreenElement) onExit(); };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, [visible, onExit]);

  if (!visible) return null;

  const currentSlide      = slides[slide] ?? null;
  const isRollupSlide     = currentSlide?.type === "rollup";
  const currentAssistantId = isRollupSlide ? null : currentSlide?.aiAssistantId;
  const currentStats      = currentAssistantId != null ? tvStats?.[currentAssistantId] : null;
  const currentInsights   = currentAssistantId != null ? tvInsights?.[currentAssistantId] ?? [] : [];
  const slideName         = isRollupSlide
    ? i18n.t("aiAssistantStats.clientSummary")
    : (currentSlide?.displayName ?? currentSlide?.name ?? "");

  // KPI data for assistant slide
  const kpiRoi    = currentStats?.roi ?? rollup?.roi;
  const kpiCounts = {
    reclamosDetectados: currentStats?.reclamosDetectados ?? 0,
    iaMessages:         currentStats?.iaMessages         ?? 0,
    humanMessages:      currentStats?.humanMessages      ?? 0,
  };

  // KPI card definitions (same logic as KpiCards.js isTV branch, inlined)
  const allKpiCards = [
    {
      flag: "horasAhorradas",
      big: true,
      value: kpiRoi?.horasAhorradas ?? 0,
      label: i18n.t("aiAssistantStats.cards.horasAhorradas"),
    },
    {
      flag: "cobertura",
      value: `${Math.round(((kpiRoi?.cobertura?.fueraHorarioPct) ?? 0) * 100)}%`,
      label: i18n.t("aiAssistantStats.cards.cobertura"),
    },
    {
      flag: "reclamosATiempo",
      value: kpiCounts.reclamosDetectados,
      label: i18n.t("aiAssistantStats.cards.reclamos"),
    },
    {
      flag: "iaVsHumano",
      value: `${kpiCounts.iaMessages}/${kpiCounts.humanMessages}`,
      label: i18n.t("aiAssistantStats.cards.iaVsHumano"),
    },
  ].filter((c) => show(c.flag));

  return (
    <div
      ref={overlayRef}
      className={classes.overlay}
      onMouseEnter={() => { pausedRef.current = true;  }}
      onMouseLeave={() => { pausedRef.current = false; }}
    >
      {/* ── Row 1: Header ─────────────────────────────────────────────── */}
      <div className={classes.header}>
        <span className={classes.slideTitle}>{slideName}</span>

        {hasMultiple && (
          <div className={classes.dotsRow}>
            {slides.map((_, i) => (
              <div
                key={i}
                className={`${classes.dot} ${i === slide ? classes.dotActive : ""}`}
                onClick={() => setSlide(i)}
              />
            ))}
          </div>
        )}

        <IconButton
          size="small"
          className={classes.exitBtn}
          onClick={() => { document.exitFullscreen?.().catch(() => {}); onExit(); }}
          title={i18n.t("aiAssistantStats.exitTV")}
        >
          <CloseIcon />
        </IconButton>
      </div>

      {/* ── Row 2A: Loading ─────────────────────────────────────────────── */}
      {loading && (
        <div className={classes.bodyCenter}>
          <CircularProgress size={48} style={{ color: WA_GREEN }} />
          <span style={{ fontSize: T.recoText, color: "rgba(255,255,255,0.4)" }}>
            {i18n.t("aiAssistantStats.empty.noData")}
          </span>
        </div>
      )}

      {/* ── Row 2B: Rollup (client summary) slide ───────────────────────── */}
      {!loading && isRollupSlide && (
        <div className={classes.bodyCenter}>
          <div className={classes.rollupTitle}>
            {i18n.t("aiAssistantStats.clientSummary")}
          </div>
          <div className={classes.rollupKpiRow}>
            {[
              { label: i18n.t("aiAssistantStats.funnel.enviados"),     v: rollup?.totals?.enviados         ?? 0 },
              { label: i18n.t("aiAssistantStats.funnel.respondieron"),  v: rollup?.totals?.respondieron     ?? 0 },
              { label: i18n.t("aiAssistantStats.kpis.tasaRespuesta"),   v: pct(rollup?.totals?.tasaRespuesta) },
              { label: i18n.t("aiAssistantStats.cards.reclamos"),       v: rollup?.totals?.reclamosDetectados ?? 0 },
            ].map(({ label, v }) => (
              <div className={classes.rollupKpiItem} key={label}>
                <div className={classes.rollupKpiValue}>{v}</div>
                <div className={classes.rollupKpiLabel}>{label}</div>
              </div>
            ))}
          </div>
          {perAssistant.length > 0 && (
            <table className={classes.rollupTable}>
              <thead>
                <tr>
                  <th className={classes.rollupTh}>{i18n.t("aiAssistantStats.table.asistente")}</th>
                  <th className={classes.rollupTh} style={{ textAlign: "right" }}>{i18n.t("aiAssistantStats.table.enviados")}</th>
                  <th className={classes.rollupTh} style={{ textAlign: "right" }}>{i18n.t("aiAssistantStats.table.tasaResp")}</th>
                  <th className={classes.rollupTh} style={{ textAlign: "right" }}>{i18n.t("aiAssistantStats.table.reclamos")}</th>
                </tr>
              </thead>
              <tbody>
                {perAssistant.map((a) => (
                  <tr key={a.aiAssistantId}>
                    <td className={classes.rollupTd}>{a.displayName ?? a.name}</td>
                    <td className={classes.rollupTd} style={{ textAlign: "right" }}>{a.enviados ?? 0}</td>
                    <td className={classes.rollupTd} style={{ textAlign: "right" }}>{pct(a.tasaRespuesta)}</td>
                    <td className={classes.rollupTd} style={{ textAlign: "right" }}>{a.reclamosDetectados ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Row 2C: Assistant slide (inner grid: 22vh KPI / 1fr zones) ──── */}
      {!loading && !isRollupSlide && currentSlide && (
        <div className={classes.assistantBody}>

          {/* ── KPI cards (22vh) ── */}
          <div className={classes.kpiSection}>
            {allKpiCards.map(({ flag, big, value, label }) => (
              <div key={flag} className={big ? classes.kpiCardBig : classes.kpiCard}>
                <div className={classes.kpiLabel}>{label}</div>
                <div className={classes.kpiValue}>{value}</div>
              </div>
            ))}
          </div>

          {/* ── 3 zones (1fr) ── */}
          <div className={classes.zonesRow}>
            {show("embudoKpis") && (
              <div className={classes.zone}>
                <div className={classes.zoneTitle}>{i18n.t("aiAssistantStats.funnel.title")}</div>
                <div className={classes.zoneContent}>
                  <TVFunnelContent stats={currentStats} />
                </div>
              </div>
            )}
            {show("recomendaciones") && (
              <div className={classes.zone}>
                <div className={classes.zoneTitle}>{i18n.t("aiAssistantStats.insights.title")}</div>
                <div className={classes.zoneContent}>
                  <TVRecoContent insights={currentInsights} />
                </div>
              </div>
            )}
            {(show("objetoReclamo") || show("tags")) && (
              <div className={classes.zone}>
                <div className={classes.zoneTitle}>
                  {i18n.t("aiAssistantStats.kpis.objetoReclamo")} /{" "}
                  {i18n.t("aiAssistantStats.kpis.tagsTrackeados")}
                </div>
                <div className={classes.zoneContent}>
                  <TVObjetoTagsContent stats={currentStats} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Row 2D: Empty (no assistants) ──────────────────────────────── */}
      {!loading && slides.length === 0 && (
        <div className={classes.bodyCenter}>
          <span style={{ fontSize: T.recoText, color: "rgba(255,255,255,0.35)" }}>
            {i18n.t("aiAssistantStats.empty.noAssistants")}
          </span>
        </div>
      )}
    </div>
  );
};

export default TVOverlay;
